import { useState, useCallback, useRef } from 'react';
import type { ImageAIProvider } from '../types/providers';
import type { OperationType } from '../types/operations';
import { HuggingFaceProvider } from '../services/providers/HuggingFaceProvider';

export type OpStatus = 'idle' | 'submitting' | 'processing' | 'succeeded' | 'failed';

interface AIOperationState {
  status: OpStatus;
  error: string | null;
  resultBlob: Blob | null;
  resultUrl: string | null;
  progress: string;
}

interface AIOperationActions {
  run(
    provider: ImageAIProvider,
    operation: OperationType,
    inputImage: Blob,
    options?: Record<string, unknown>
  ): Promise<Blob | null>;
  reset(): void;
  cancel(): void;
}

export function useAIOperation(): [AIOperationState, AIOperationActions] {
  const [status, setStatus] = useState<OpStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState('');
  const cancelledRef = useRef(false);
  const predictionIdRef = useRef<string | null>(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setResultBlob(null);
    setResultUrl(null);
    setProgress('');
    cancelledRef.current = false;
    predictionIdRef.current = null;
  }, [resultUrl]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const run = useCallback(async (
    provider: ImageAIProvider,
    operation: OperationType,
    inputImage: Blob,
    options?: Record<string, unknown>
  ): Promise<Blob | null> => {
    cancelledRef.current = false;
    setStatus('submitting');
    setError(null);
    setResultBlob(null);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setResultUrl(null);
    setProgress('Submitting...');

    try {
      const predictionId = await provider.submit(operation, inputImage, options);
      predictionIdRef.current = predictionId;

      if (cancelledRef.current) return null;
      setStatus('processing');
      setProgress('Processing...');

      // Poll for result
      const maxAttempts = 120; // 6 minutes at 3s intervals
      for (let i = 0; i < maxAttempts; i++) {
        if (cancelledRef.current) {
          provider.cancel(predictionId).catch(() => {});
          setStatus('idle');
          return null;
        }

        const result = await provider.poll(predictionId);

        if (result.status === 'succeeded' && result.output?.length) {
          // For HuggingFace, get the blob directly
          if (provider instanceof HuggingFaceProvider) {
            const blob = provider.getResultBlob(predictionId);
            if (blob && blob.size > 0) {
              const url = URL.createObjectURL(blob);
              setResultBlob(blob);
              setResultUrl(url);
              setStatus('succeeded');
              setProgress('');
              provider.clearResult(predictionId);
              return blob;
            }
          }

          // For Replicate and others, fetch the output URL
          const outputUrl = result.output[0];
          const res = await fetch(outputUrl);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          setResultBlob(blob);
          setResultUrl(url);
          setStatus('succeeded');
          setProgress('');
          return blob;
        }

        if (result.status === 'failed') {
          throw new Error(result.error || 'Operation failed');
        }

        // Still processing — update progress
        const elapsed = (i + 1) * 3;
        setProgress(`Processing... (${elapsed}s)`);

        // Wait before polling again
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      throw new Error('Operation timed out after 6 minutes');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setStatus('failed');
      setProgress('');
      return null;
    }
  }, [resultUrl]);

  return [
    { status, error, resultBlob, resultUrl, progress },
    { run, reset, cancel },
  ];
}
